import { useTranslation } from 'react-i18next';
import Typography from '@lib/components/Typography/Typography';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import CountMark, {
  CountMarkSizes,
  CountMarkTypes,
} from '@lib/components/CountMark/CountMark';
import AssistantRow from './AssistantRow';
import getAiToolLabel from './utils/getAiToolLabel';
import groupToolStepsByCategory from './utils/groupToolStepsByCategory';
import styles from './AiAssistant.module.scss';

type ToolCall = {
  id: string;
  toolName: string;
};

type Props = {
  toolCalls: ToolCall[];
};

// The completed-activity lines on their own, without the assistant lane around
// them — so a turn that ran tools and then failed can stack them above its
// failure banner inside a single avatar group (see AiErrorBanner).
export function ToolActivityLines({ toolCalls }: Props) {
  const { t } = useTranslation();
  const groups = groupToolStepsByCategory(toolCalls);
  return (
    <>
      {groups.map((group) => (
        <div key={group.key} className={styles.toolActivity}>
          <MaterialIcon
            icon="check_circle"
            size="md-20"
            className={styles.toolActivityIcon}
          />
          <Typography variant="caption">
            {getAiToolLabel(group.step.toolName, t)}
          </Typography>
          {group.count > 1 && (
            <CountMark
              size={CountMarkSizes.small}
              type={CountMarkTypes.blue}
              value={group.count}
            />
          )}
        </div>
      ))}
    </>
  );
}

// A persisted tool-activity transcript row (role ASSISTANT, empty content,
// non-empty toolCalls — see datizy-api/docs/fe/ai_frontend_guide.md §5). Rendered as a
// compact completed-activity line rather than a chat bubble.
function ToolActivityRow({ toolCalls }: Props) {
  if (!toolCalls.length) return null;
  return (
    <AssistantRow>
      <ToolActivityLines toolCalls={toolCalls} />
    </AssistantRow>
  );
}

export default ToolActivityRow;
